import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Countdown from "../components/Countdown";
import { ASSETS } from "../lib/api";
import { MapPin, Clock, Palette, ExternalLink } from "lucide-react";

const VENUE_MAP_URL = "https://www.google.com/maps/search/?api=1&query=Balmoral+Convention+Center+Federal+Palace+Hotel+Ahmadu+Bello+Way+Victoria+Island+Lagos";

// Small scroll-reveal helper — fades + rises children into view once.
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity .9s cubic-bezier(.2,.7,.2,1) ${delay}ms, transform .9s cubic-bezier(.2,.7,.2,1) ${delay}ms`,
      }}
    >{children}</div>
  );
};

const Detail = ({ icon: Icon, label, children, testId }) => (
  <div className="border-t border-hairline pt-6" data-testid={testId}>
    <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold">
      <Icon size={13} strokeWidth={1.4} /> {label}
    </div>
    <div className="mt-3 font-serif text-2xl text-ink leading-tight">{children}</div>
  </div>
);

const Traditional = () => (
  <div data-testid="traditional-page">
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-14">
      <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-6">Save The Date</div>
      <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-ink max-w-4xl">
        Anjola &amp; Michael&rsquo;s <em className="text-gold not-italic italic">Traditional</em> Wedding Ceremony
      </h1>
      <p className="mt-6 text-base sm:text-lg text-ink-soft max-w-xl">
        Two hearts. Two families. One beautiful celebration.
      </p>
    </section>

    {/* Video */}
    <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
      <div className="flex justify-center">
        <div className="video-frame w-full max-w-[360px] sm:max-w-[400px]">
          <video
            data-testid="traditional-video"
            autoPlay loop muted playsInline preload="auto"
            poster={ASSETS.tradFacing}
            className="w-full aspect-[9/16] object-cover bg-ink"
          >
            <source src={ASSETS.videoTraditional} type="video/mp4" />
          </video>
        </div>
      </div>
      <div className="text-center text-[10px] tracking-[0.3em] uppercase text-gold mt-6">Our Save-the-Date</div>
    </section>

    {/* Details — with clickable venue card */}
    <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <Reveal>
          <img src={ASSETS.tradFacing} alt="Anjola and Michael in traditional attire" loading="lazy" className="w-full aspect-[4/5] object-cover" />
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:pl-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">The Ceremony</div>
        <div className="grid sm:grid-cols-2 gap-8">
          <Reveal delay={100}>
            <div className="border-t border-hairline pt-6" data-testid="detail-date">
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold">Date</div>
              <div className="mt-3 font-serif text-3xl text-ink leading-none">May 8, 2027</div>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <a
              href={VENUE_MAP_URL}
              target="_blank" rel="noopener noreferrer"
              data-testid="detail-venue"
              className="group block border-t border-hairline pt-6 hover:border-gold transition-colors focus:outline-none"
            >
              <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold">
                <MapPin size={13} strokeWidth={1.4} /> Venue
                <ExternalLink size={11} strokeWidth={1.6} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </div>
              <div className="mt-3 font-serif text-2xl text-ink leading-tight group-hover:text-gold-hover transition-colors">
                Balmoral Convention Center
              </div>
              <div className="text-sm text-ink-soft font-sans mt-2 leading-relaxed">
                at Federal Palace Hotel &amp; Casino<br />
                6–8 Ahmadu Bello Way,<br />
                Victoria Island, Lagos 101241,<br />
                Nigeria
              </div>
              <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-gold">
                Tap to open in Maps →
              </div>
            </a>
          </Reveal>
          <Reveal delay={300}>
            <Detail icon={Clock} label="Guest Arrival" testId="detail-arrival">
              <span className="text-lg text-ink-soft font-sans">Guest arrival time will be shared soon.</span>
            </Detail>
          </Reveal>
          <Reveal delay={400}>
            <Detail icon={Palette} label="Dress Code" testId="detail-dress">
              <span className="text-lg text-ink-soft font-sans">Dress code and color details will be shared soon.</span>
            </Detail>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Dress Code / Mood Board placeholder */}
    <section className="bg-ivory border-y border-hairline" data-testid="mood-board-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <Reveal>
          <div className="hairline mb-12"><span>Dress Code · Mood</span></div>
        </Reveal>
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <Reveal delay={100}>
              <img src={ASSETS.tradEmbrace} alt="Traditional attire close" loading="lazy" className="w-full aspect-[4/5] object-cover" />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={200}>
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">Dress Code</div>
              <h3 className="font-serif text-3xl sm:text-4xl text-ink leading-tight tracking-tight">
                A palette befitting the day.
              </h3>
              <p className="mt-6 text-ink-soft leading-relaxed max-w-lg">
                The final dress code and color story will be shared here closer to the date, along with a mood board of inspirations. In the meantime, expect elegance, warmth, and celebration.
              </p>
              <div className="mt-8 grid grid-cols-4 gap-3 max-w-md" data-testid="palette-placeholders">
                <div className="aspect-square bg-champagne border border-hairline" title="Champagne" />
                <div className="aspect-square bg-cream border border-hairline" title="Cream" />
                <div className="aspect-square border border-hairline" style={{ background: "#B08D57" }} title="Warm gold" />
                <div className="aspect-square border border-hairline" style={{ background: "#7A2E2E" }} title="Whisper burgundy" />
              </div>
              <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-ink-faint">
                Palette to be confirmed
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    {/* Copy block */}
    <section className="bg-cream/60 border-y border-hairline">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
        <div className="hairline mb-10"><span>A · M</span></div>
        <div className="text-ink-soft leading-[1.9] text-base sm:text-lg space-y-5">
          <p>Two hearts. Two families. One beautiful celebration.</p>
          <p>
            With grateful hearts, the families of <span className="text-ink">Bola &amp; Shola Temowo</span> and
            {" "}<span className="text-ink">Mike &amp; Susan Itemuagbor</span> are delighted to announce the upcoming
            Traditional Wedding Ceremony of their beloved children, <span className="font-serif italic text-ink">Anjola and Michael</span>.
          </p>
          <p>
            Please save the date for <span className="text-ink">May 8, 2027</span>, and kindly confirm your attendance by completing the RSVP form.
          </p>
          <p>Your early RSVP will help us thoughtfully plan a memorable celebration for all our cherished guests.</p>
          <p>We look forward to celebrating, sharing joyful moments, and creating beautiful memories together.</p>
          <p className="pt-4 font-serif italic text-ink">With love,</p>
          <p className="text-gold text-sm tracking-[0.2em] uppercase">The Temowo &amp; Itemuagbor Families</p>
        </div>
      </div>
    </section>

    {/* Countdown + CTA */}
    <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">Countdown · Traditional Ceremony</div>
          <div className="font-serif text-4xl sm:text-5xl text-ink leading-none tracking-tight">
            The wait is nearly over.
          </div>
          <div className="mt-4 text-xs text-ink-faint tracking-[0.15em] uppercase">May 8, 2027 · Balmoral Convention Center</div>
        </div>
        <div className="lg:col-span-6"><Countdown /></div>
      </div>
      <div className="mt-14 flex flex-wrap gap-3">
        <Link
          to="/rsvp"
          data-testid="traditional-rsvp-btn"
          className="btn-gold inline-flex items-center justify-center border border-gold text-gold text-[11px] tracking-[0.25em] uppercase px-8 py-4 rounded-full"
        >
          RSVP for the Traditional Wedding
        </Link>
      </div>
    </section>

    {/* Seated portrait finale */}
    <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
      <Reveal>
        <img src={ASSETS.tradSeated} alt="Anjola and Michael seated in traditional attire" loading="lazy" className="w-full aspect-[4/5] sm:aspect-[16/10] object-cover" style={{ objectPosition: "50% 25%" }} />
      </Reveal>
    </section>
  </div>
);

export default Traditional;
